import React from 'react';
import { useSelectedLocationData, RecordDataState, isRecordFound } from '../../../../hooks/data-hooks';
import { LocationDataProvider } from '../../../../data/DataProviders/LocationDataProvider';
import { ContentContainer } from '../../../../components/ContentContainer';
import { DataProviderCache } from '../../../../data/DataProviders/DataProviderCache';
import { RecordDataIndicators } from '../../../../components/RecordDataIndicators';
import { useLanguageLocale } from '../../../../hooks/language-locale-hooks';
import { OverviewSiteSummary } from './OverviewSiteSummary';
import { Route, Switch } from 'react-router';
import { useHistory } from 'react-router-dom';
import { AppRoutes, getAppRouteCacheName } from '../../../../routes';
import { OverviewSiteDevices } from './all-devices/OverviewSiteDevices';
import { OverviewSiteReadingDetails } from './readings/OverviewSiteReadingDetails';
import { GenericToolbar } from '../../../../components/GenericToolbar/GenericToolbar';
import { pushAppRoute } from '../../../../data/domain/route-manager';
import { TimelineDataProvider } from '../../../../data/DataProviders/TimelineDataProvider';
import { OverviewSiteTimeline } from './timeline/OverviewSiteTimeline';
import { OverviewSiteDeviceDetails } from '../devices/OverviewSiteDeviceDetails';
import { OverviewSiteDevicesDeviceDetails } from './all-devices/OverviewSiteDevicesDeviceDetails';
import { SiteDevicesDataProvider } from '../../../../data/DataProviders/SiteDevicesDataProvider';
import { SiteChannelDataProvider } from '../../../../data/DataProviders/SiteChannelDataProvider';
import { SiteOnlineStatusDataProvider } from '../../../../data/DataProviders/SiteOnlineStatusDataProvider';
import { OverviewSiteEventDetails } from '../events/OverviewSiteEventDetails';
import { OverviewSiteTimelineEventDetails } from './timeline/events/OverviewSiteTimelineEventDetails';
import { OverviewSiteTrends } from './trends/OverviewSiteTrends';
import { DeviceDataProvider } from '../../../../data/DataProviders/DeviceDataProvider';
import { TrendDataProvider } from '../../../../data/DataProviders/TrendDataProvider';
import { TimelineEventDetailDataProvider } from '../../../../data/DataProviders/TimelineEventDetailDataProvider';
import { useCurrentAppRoute } from '../../../../hooks/app-route-hooks';
import { SiteAlarmCountProvider } from '../../../../data/DataProviders/SiteAlarmCountProvider';
import { SiteBatteryStatusDataProvider } from '../../../../data/DataProviders/SiteBatteryStatusDataProvider';
import { SiteAlarmListProvider } from '../../../../data/DataProviders/SiteAlarmListProvider';
import { SignalRSiteDataProvider } from '../../../../data/DataProviders/SignalRSiteDataProvider';
import { OverviewSiteDeviceDetailsTabView } from '../devices/OverviewDeviceDetailTabView';
import { useGetOrganizationCheckDetails } from '../../../../data/DataProviders/GetOrgCheckProvider';
import { isDataLoaded } from '../../../../types';

import { GetSiteSubscriptionProvider } from '../../../../data/DataProviders/GetSiteSubscriptionProvider';
const SummaryDataProvider: React.FC<{
    siteId: string;
    timeline?: { limit?: number; offset?: number };
    channelData?: { limit?: number; priority?: boolean };
}> = (props) => (
    <SiteOnlineStatusDataProvider siteId={props.siteId}>
        <SiteAlarmCountProvider siteId={props.siteId}>
            <SiteDevicesDataProvider siteId={props.siteId} recursive={true} includeflag={true}>
                <SiteBatteryStatusDataProvider siteId={props.siteId}>
                    <SiteChannelDataProvider
                        siteId={props.siteId}
                        limit={props.channelData ? props.channelData.limit : undefined}
                        priority={props.channelData ? props.channelData.priority : undefined}
                    >
                        <TimelineDataProvider
                            organizationIdOrLocationIdOrSiteId={props.siteId}
                            limit={props.timeline ? props.timeline.limit : undefined}
                        >
                            {props.children}
                        </TimelineDataProvider>
                    </SiteChannelDataProvider>
                </SiteBatteryStatusDataProvider>
            </SiteDevicesDataProvider>
        </SiteAlarmCountProvider>
    </SiteOnlineStatusDataProvider>
);

const SiteReadingDetailsDataProvider: React.FC<{ siteId: string }> = (props) => (
    <SiteOnlineStatusDataProvider siteId={props.siteId}>
        <SiteDevicesDataProvider siteId={props.siteId} recursive={true} includeflag={false}>
            <SiteChannelDataProvider siteId={props.siteId}>{props.children}</SiteChannelDataProvider>
        </SiteDevicesDataProvider>
    </SiteOnlineStatusDataProvider>
);

const SiteTimelineDataProvider: React.FC<{ siteId: string; limit?: number; offset?: number }> = (props) => (
    <TimelineDataProvider organizationIdOrLocationIdOrSiteId={props.siteId} limit={props.limit}>
        {props.children}
    </TimelineDataProvider>
);

export const OverviewSite: React.FC = () => {
    const { t } = useLanguageLocale();
    const history = useHistory();
    const selectedLocationData = useSelectedLocationData();
    const currentAppRoute = useCurrentAppRoute();
    const getOrgCheckDetails = useGetOrganizationCheckDetails();
    const [getOrgTypeData, setgetOrgTypeData] = React.useState<any>();

    if (!('siteId' in currentAppRoute)) {
        throw new Error('We should never be here');
    }
    React.useEffect(() => {
        if (isDataLoaded(getOrgCheckDetails)) {
            setgetOrgTypeData(getOrgCheckDetails.value);
        }
    }, [getOrgCheckDetails]);

    return (
        <>
            {selectedLocationData.state !== RecordDataState.Found && (
                <>
                    <GenericToolbar
                        showBackButton
                        backButtonOnClick={(): void => {
                            pushAppRoute(history, {
                                type: 'OverviewLocation',
                                locationId: currentAppRoute.locationId,
                            });
                        }}
                    />

                    <ContentContainer>
                        <RecordDataIndicators
                            recordData={selectedLocationData}
                            errorMessage={t('DASHBOARD.ERROR_LOADING_LOCATION')}
                            notFoundIcon="LocationCity"
                            notFoundMessage={t('DASHBOARD.LOCATION_NOT_FOUND')}
                        />
                    </ContentContainer>
                </>
            )}

            {isRecordFound(selectedLocationData) && (getOrgTypeData || getOrgTypeData === undefined) && (
                <LocationDataProvider locationId={selectedLocationData.value.id}>
                    <SiteAlarmListProvider locationId={selectedLocationData.value.id}>
                        <DataProviderCache for={getAppRouteCacheName(AppRoutes.OverviewSite)}>
                            <Switch>
                                <Route exact path={AppRoutes.OverviewSite}>
                                    <SummaryDataProvider
                                        siteId={currentAppRoute.siteId}
                                        timeline={{ limit: 5 }}
                                        channelData={{ limit: 14 }}
                                    >
                                        <SignalRSiteDataProvider
                                            siteId={currentAppRoute.siteId}
                                            locationId={selectedLocationData.value.id}
                                        >
                                            <GetSiteSubscriptionProvider
                                                siteId={currentAppRoute.siteId}
                                                org={getOrgTypeData === undefined ? undefined : getOrgTypeData.isChamp}
                                            >
                                                <OverviewSiteSummary />
                                            </GetSiteSubscriptionProvider>
                                        </SignalRSiteDataProvider>
                                    </SummaryDataProvider>
                                </Route>

                                <Route exact path={AppRoutes.OverviewSiteReadings}>
                                    <DataProviderCache for={getAppRouteCacheName(AppRoutes.OverviewSiteReadings)}>
                                        <SiteReadingDetailsDataProvider siteId={currentAppRoute.siteId}>
                                            <SignalRSiteDataProvider
                                                siteId={currentAppRoute.siteId}
                                                locationId={selectedLocationData.value.id}
                                            >
                                                <OverviewSiteReadingDetails />
                                            </SignalRSiteDataProvider>
                                        </SiteReadingDetailsDataProvider>
                                    </DataProviderCache>
                                </Route>

                                <Route path={AppRoutes.OverviewSiteEventDetails}>
                                    {'alarmId' in currentAppRoute && 'deviceId' in currentAppRoute && (
                                        <DataProviderCache
                                            for={getAppRouteCacheName(AppRoutes.OverviewSiteEventDetails)}
                                        >
                                            <TimelineEventDetailDataProvider
                                                alarmId={currentAppRoute.alarmId}
                                                deviceId={currentAppRoute.deviceId}
                                            >
                                                <DeviceDataProvider deviceId={currentAppRoute.deviceId}>
                                                    <OverviewSiteEventDetails />
                                                </DeviceDataProvider>
                                            </TimelineEventDetailDataProvider>
                                        </DataProviderCache>
                                    )}
                                </Route>

                                <Route exact path={AppRoutes.TimelineSiteTabDetails}>
                                    {'deviceId' in currentAppRoute && (
                                        <DataProviderCache for={getAppRouteCacheName(AppRoutes.TimelineSiteTabDetails)}>
                                            <DeviceDataProvider deviceId={currentAppRoute.deviceId}>
                                                <GetSiteSubscriptionProvider
                                                    siteId={currentAppRoute.siteId}
                                                    org={
                                                        getOrgTypeData === undefined
                                                            ? undefined
                                                            : getOrgTypeData.isChamp
                                                    }
                                                >
                                                    <OverviewSiteDeviceDetailsTabView />
                                                </GetSiteSubscriptionProvider>
                                            </DeviceDataProvider>
                                        </DataProviderCache>
                                    )}
                                </Route>

                                <Route exact path={AppRoutes.OverviewSiteDeviceDetails}>
                                    {'deviceId' in currentAppRoute && (
                                        <DataProviderCache
                                            for={getAppRouteCacheName(AppRoutes.OverviewSiteDeviceDetails)}
                                        >
                                            <DeviceDataProvider deviceId={currentAppRoute.deviceId}>
                                                <OverviewSiteDeviceDetails />
                                            </DeviceDataProvider>
                                        </DataProviderCache>
                                    )}
                                </Route>

                                <Route exact path={AppRoutes.OverviewSiteTrends}>
                                    <DataProviderCache for="Overview Site Trends">
                                        <TrendDataProvider siteId={currentAppRoute.siteId}>
                                            {/* <GetPartNumberProvider
                                                siteId={currentAppRoute.siteId}
                                                org={getOrgTypeData === undefined ? undefined : getOrgTypeData.isChamp}
                                            > */}
                                            <GetSiteSubscriptionProvider
                                                siteId={currentAppRoute.siteId}
                                                org={getOrgTypeData === undefined ? undefined : getOrgTypeData.isChamp}
                                            >
                                                <OverviewSiteTrends />
                                            </GetSiteSubscriptionProvider>
                                            {/* </GetPartNumberProvider> */}
                                        </TrendDataProvider>
                                    </DataProviderCache>
                                </Route>

                                <Route path={AppRoutes.OverviewSiteDevices}>
                                    <DataProviderCache for={getAppRouteCacheName(AppRoutes.OverviewSiteDevices)}>
                                        <Switch>
                                            <Route exact path={AppRoutes.OverviewSiteDevices}>
                                                <SiteDevicesDataProvider
                                                    siteId={currentAppRoute.siteId}
                                                    recursive={true}
                                                    includeflag={true}
                                                >
                                                    <OverviewSiteDevices />
                                                </SiteDevicesDataProvider>
                                            </Route>
                                            <Route exact path={AppRoutes.OverviewSiteDevicesDeviceDetails}>
                                                {'deviceId' in currentAppRoute && (
                                                    <DataProviderCache
                                                        for={getAppRouteCacheName(
                                                            AppRoutes.OverviewSiteDevicesDeviceDetails
                                                        )}
                                                    >
                                                        <DeviceDataProvider deviceId={currentAppRoute.deviceId}>
                                                            <OverviewSiteDevicesDeviceDetails />
                                                        </DeviceDataProvider>
                                                    </DataProviderCache>
                                                )}
                                            </Route>
                                        </Switch>
                                    </DataProviderCache>
                                </Route>

                                <Route path={AppRoutes.OverviewSiteTimeline}>
                                    <DataProviderCache for={getAppRouteCacheName(AppRoutes.OverviewSiteTimeline)}>
                                        <Switch>
                                            <Route exact path={AppRoutes.OverviewSiteTimeline}>
                                                <SiteTimelineDataProvider siteId={currentAppRoute.siteId}>
                                                    <SignalRSiteDataProvider
                                                        siteId={currentAppRoute.siteId}
                                                        locationId={selectedLocationData.value.id}
                                                    >
                                                        <OverviewSiteTimeline />
                                                    </SignalRSiteDataProvider>
                                                </SiteTimelineDataProvider>
                                            </Route>
                                            <Route exact path={AppRoutes.OverviewSiteTimelineEventDetails}>
                                                {'alarmId' in currentAppRoute && 'deviceId' in currentAppRoute && (
                                                    <DataProviderCache
                                                        for={getAppRouteCacheName(
                                                            AppRoutes.OverviewSiteTimelineEventDetails
                                                        )}
                                                    >
                                                        <TimelineEventDetailDataProvider
                                                            alarmId={currentAppRoute.alarmId}
                                                            deviceId={currentAppRoute.deviceId}
                                                        >
                                                            <DeviceDataProvider deviceId={currentAppRoute.deviceId}>
                                                                <OverviewSiteTimelineEventDetails />
                                                            </DeviceDataProvider>
                                                        </TimelineEventDetailDataProvider>
                                                    </DataProviderCache>
                                                )}
                                            </Route>
                                        </Switch>
                                    </DataProviderCache>
                                </Route>
                            </Switch>
                        </DataProviderCache>
                    </SiteAlarmListProvider>
                </LocationDataProvider>
            )}
        </>
    );
};
